import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from '@angular/core';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { FileNameService } from '../../../../core/services';
import { ReportWorkspaceService } from '../../report-workspace.service';
import { HedgeRow } from './hedge-coverage.report';

type MetricKeys =
    | 'deliveryCurvePercent'
    | 'shortCapVolume'
    | 'shortFixedVolume'
    | 'longCapVolume'
    | 'longFixedVolume'
    | 'longCapPremium'
    | 'capDifference'
    | 'fixedDifference';

interface MetricDef {
    metricKey: MetricKeys;
    label: string;
    valueFormat?: 'number' | 'percent';
}

type PivotCell = { volume: number; price?: number } | number;
type PivotRow = {
    metricKey: string;
    label: string;
    addSpaceAfter?: boolean;
    [periodKey: string]: PivotCell | string | boolean | undefined;
};

type Column = {
    field: string;
    header: string;
};

interface Period {
    periodKey: string; //YYYYMM
    year: number;
    month: number;
    label: string; // Jan-26
}

@Component({
    selector: 'app-hedge-coverage-data',
    imports: [CommonModule, ProgressSpinner, TableModule],
    templateUrl: './hedge-coverage-data.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HedgeCoverageDataComponent {
    private reportWorkspace = inject(ReportWorkspaceService);
    private fileNameService = inject(FileNameService);

    reportData = this.reportWorkspace.reportData;
    isLoading = this.reportWorkspace.isReportRunning;

    includeLongCapPremiums = computed(() =>
        this.reportWorkspace.parameterValues()['includeLongCapPremiums'] ?? false
    );
    volumeMetrics = new Set([
        'shortCapVolume',
        'shortFixedVolume',
        'longCapVolume',
        'longFixedVolume'
    ]);

    readonly METRIC_DEFS: Record<MetricKeys, MetricDef> = {
        deliveryCurvePercent: { metricKey: 'deliveryCurvePercent', label: 'Delivery Curve %', valueFormat: 'percent' },
        shortCapVolume: { metricKey: 'shortCapVolume', label: 'Short Cap Total' },
        shortFixedVolume: { metricKey: 'shortFixedVolume', label: 'Short Fixed Total' },
        longCapVolume: { metricKey: 'longCapVolume', label: 'Long Cap Total' },
        longFixedVolume: { metricKey: 'longFixedVolume', label: 'Long Fixed Total' },
        capDifference: { metricKey: 'capDifference', label: 'Cap Difference' },
        fixedDifference: { metricKey: 'fixedDifference', label: 'Fixed Difference' },
        longCapPremium: { metricKey: 'longCapPremium', label: 'Premium' }
    };

    readonly BASE_METRIC_ORDER: MetricKeys[] = [
        'deliveryCurvePercent',
        'shortCapVolume',
        'shortFixedVolume',
        'longCapVolume',
        'longFixedVolume',
        'capDifference',
        'fixedDifference',
        'longCapPremium'
    ];

    private getPrice(metricKey: MetricKeys, row: HedgeRow): number | undefined {
        switch (metricKey) {
            case 'shortCapVolume':
                return row.shortCapAveragePrice;
            case 'shortFixedVolume':
                return row.shortFixedAveragePrice;
            case 'longCapVolume':
                return row.averageCapStrikePrice;
            case 'longFixedVolume':
                return row.averageFixedStrikePrice;
            default:
                return undefined;
        }
    }

    metricOrder = computed(() => {
        if (this.includeLongCapPremiums()) {
            return this.BASE_METRIC_ORDER;
        }

        return this.BASE_METRIC_ORDER.filter(m => m !== 'longCapPremium');
    });

    periods = computed<Period[]>(() => {
        const reportData = this.reportData() as HedgeRow[];
        return reportData
            .map(r => ({
                periodKey: (r.year * 100 + r.month).toString(),
                year: r.year,
                month: r.month,
                label: r.monthYear
            }))
            .sort((a, b) => Number(a.periodKey) - Number(b.periodKey));
    });

    isDifferenceRow(metric: string): boolean {
        return metric === 'capDifference' || metric === 'fixedDifference';
    }

    isVolumeCell(value: unknown): value is { volume: number; price?: number } {
        return typeof value === 'object' && value !== null && 'volume' in value;
    }

    getPeriodKey(row: HedgeRow): string {
        return `${row.year}-${row.month.toString().padStart(2, '0')}`;
    }

    pivotRows = computed<PivotRow[]>(() => {

        const reportData = this.reportData() as HedgeRow[];
        if (!reportData.length) return [];

        const includePremium = this.includeLongCapPremiums();
        const rows: PivotRow[] = [];

        const firstPeriodKey = this.getPeriodKey(reportData[0]);

        for (const metricKey of this.metricOrder()) {

            if (metricKey === 'longCapPremium') continue;

            const config = this.METRIC_DEFS[metricKey];

            const pivotRow: PivotRow = {
                metricKey,
                label: config.label,
                addSpaceAfter: metricKey === 'longFixedVolume'
            };

            for (const row of reportData) {

                const value = row[metricKey as keyof HedgeRow];
                if (value === undefined || value === null) continue;

                const periodKey = this.getPeriodKey(row);

                if (this.volumeMetrics.has(metricKey)) {

                    pivotRow[periodKey] = {
                        volume: value as number,
                        price: this.getPrice(metricKey, row)
                    };

                } else {

                    pivotRow[periodKey] = value as number;

                }
            }

            rows.push(pivotRow);

            // Insert Premium row after Long Fixed Total
            if (metricKey === 'longCapVolume' && includePremium) {

                const premiumRow: PivotRow = {
                    metricKey: 'longCapPremium',
                    label: this.METRIC_DEFS.longCapPremium.label
                };

                for (const row of reportData) {

                    const periodKey = this.getPeriodKey(row);

                    premiumRow[periodKey] = {
                        volume: row.averageCapUnitCost ?? 0
                    };
                }

                rows.push(premiumRow);
            }
        }

        const netCapTotal = reportData.reduce((s, r) => s + (r.capDifference ?? 0), 0);
        const netFixedTotal = reportData.reduce((s, r) => s + (r.fixedDifference ?? 0), 0);

        rows.push(
            {
                metricKey: 'netCapDifference',
                label: 'Net Cap Difference',
                [firstPeriodKey]: { volume: netCapTotal }
            },
            {
                metricKey: 'netFixedDifference',
                label: 'Net Fixed Difference',
                [firstPeriodKey]: { volume: netFixedTotal }
            }
        );

        return rows;
    });

    columns = computed<Column[]>(() => {
        const periods = this.periods();
        const cols: Column[] = [
            { field: 'label', header: 'Metric' }
        ];

        for (const p of periods) {
            cols.push({
                field: `${p.year}-${p.month.toString().padStart(2, '0')}`,
                header: p.label
            });
        }
        return cols;
    });

    constructor() {
        /* Export Request */
        effect(() => {
            const exportRequest = this.reportWorkspace.exportRequest();

            if (exportRequest === null) return;

			let exportFileName = '';
			untracked(() => {
				const isSysAdmin = this.reportWorkspace.isSysAdmin();
				const subscriberName = this.reportWorkspace.subscriber()?.subscriberName ?? '';
				const reportName = this.reportWorkspace.reportName() ?? '';

                exportFileName = this.fileNameService.buildReportFileName(
                    isSysAdmin,
                    subscriberName,
                    reportName
                );
            });

            try {
                this.exportCSV(exportFileName);
            } catch (error) {
                console.error(error);
            }

            this.reportWorkspace.setExportRequest(null);
        });
    }

    /**
     * Formats a single cell for export to match table display
     */
    private formatExportCell(row: PivotRow, field: string): string {
        const value = row[field];
        if (value == null) return '';

        // Volume cell with optional price
        if (typeof value === 'object' && 'volume' in value) {
            // Special case for Premium row
            if (row.metricKey === 'longCapPremium') {
                return `$${Number(value.volume).toFixed(2)}`; // always 2 decimals
            }

            const vol = Number(value.volume ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
            if (value.price != null) {
                const price = Number(value.price).toFixed(2);
                return `${vol} ($${price})`;
            }
            return vol;
        }

        // Simple number cell
        if (typeof value === 'number') {
            const metric = this.getMetricDef(row.metricKey as MetricKeys);
            if (metric?.valueFormat === 'percent') {
                return `${(value * 100).toFixed(0)}%`;
            }
            return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        return String(value);
    }

    /**
     * Exports CSV manually to match table formatting
     */
    private exportCSV(fileName: string) {
        const rows = this.pivotRows();
        const columns = this.columns();

        if (!rows.length || !columns.length) return;

        // CSV header
        const header = columns.map(c => `"${c.header}"`).join(',');

        // CSV body
        const body = rows.map(row =>
            columns.map(col => `"${this.formatExportCell(row, col.field)}"`).join(',')
        );

        const csv = [header, ...body].join('\r\n');

        // Trigger download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName || 'HedgeCoverage.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    getMetricDef(key: MetricKeys) {
        return this.METRIC_DEFS[key];
    }
}
